import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth, API } from "@/App";
import { tokenStore } from "@/lib/token";
import axios from "axios";
import { toast } from "sonner";
import { 
  Home,
  Users,
  RefreshCw,
  Settings,
  Radio,
  ChevronDown,
  Plus,
  Trash2,
  Server,
  Hash,
  Crown,
  ShoppingBag,
  ArrowLeft,
  CreditCard,
  Check,
  AlertTriangle,
  Shield,
  KeyRound,
  Pencil,
  Save
} from "lucide-react";
import Navbar from "@/components/Navbar";
import TokenSwitchStatus from "@/components/TokenSwitchStatus";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Bot type configurations
const BOT_DISPLAY = {
  gestion: {
    logo: "/favicon.png",
    colorClass: "text-blue-500",
    bgClass: "bg-blue-500/10",
    borderClass: "border-blue-500/30",
    btnClass: "bg-blue-500 hover:bg-blue-600",
    icon: Settings,
    priceOffset: 0
  },
  radio: {
    logo: "/favicon.png",
    colorClass: "text-green-500",
    bgClass: "bg-green-500/10",
    borderClass: "border-green-500/30",
    btnClass: "bg-green-500 hover:bg-green-600",
    icon: Radio,
    priceOffset: -0.50
  }
};

// Pricing plans
const BASE_PRICING = [
  { id: "basique", name: "Basique", basePrice: 2, duration: "1 mois", badge: "🥉", days: 30 },
  { id: "standard", name: "Standard", basePrice: 4, duration: "2 mois", badge: "🥈", savings: "10%", days: 60 },
  { id: "premium", name: "Premium", basePrice: 8, duration: "4 mois", badge: "🥇", savings: "20%", days: 120 },
  { id: "gold", name: "Gold", basePrice: 16, duration: "8 mois", badge: "💎", savings: "33%", days: 240 },
  { id: "platinum", name: "Platinum", basePrice: 24, duration: "12 mois", badge: "👑", savings: "50%", days: 365 },
  { id: "lifetime", name: "Lifetime", basePrice: 46, duration: "À vie", badge: "♾️", savings: "Meilleur investissement", days: 36500 },
];

// Navigation items
const NAV_ITEMS = [
  { id: "accueil", label: "Accueil", icon: Home },
  { id: "moderation", label: "Modération", icon: Users },
  { id: "parametres", label: "Paramètres", icon: Settings },
  { id: "renouvellement", label: "Renouvellement", icon: RefreshCw },
];

// Logo du bot : celui réglé sur le Discord Developer Portal, sinon le logo du produit.
const botLogo = (bot) => bot?.bot_avatar_url || (BOT_DISPLAY[bot?.bot_type] || BOT_DISPLAY.gestion).logo;

export default function BotManagementPage() {
  const { botId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [bots, setBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState(null);
  const [activeSection, setActiveSection] = useState("accueil");
  const [loading, setLoading] = useState(true);
  
  // Moderation state
  const [owners, setOwners] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [newOwnerId, setNewOwnerId] = useState("");
  const [newBuyerId, setNewBuyerId] = useState("");
  const [deleteUser, setDeleteUser] = useState(null);
  
  // Renewal state
  const [selectedPlan, setSelectedPlan] = useState("premium");
  const [renewLoading, setRenewLoading] = useState(false);
  
  // Serveurs où le bot se trouve, lus sur Discord avec son token.
  const [servers, setServers] = useState([]);
  const [tokenInvalid, setTokenInvalid] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Paramètres : nom et token
  const [nameInput, setNameInput] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [tokenSaving, setTokenSaving] = useState(false);

  // Met à jour le bot affiché (logo, nom...) avec ce que renvoie le serveur.
  const mergeBot = useCallback((fresh) => {
    if (!fresh?.id) return;
    const pick = (b) => (b.id === fresh.id ? { ...b, ...fresh } : b);
    setBots((current) => current.map(pick));
    setSelectedBot((current) => (current && current.id === fresh.id ? { ...current, ...fresh } : current));
  }, []);

  const fetchBotDetails = useCallback(async (id, refresh = false) => {
    try {
      const token = tokenStore.get();
      const res = await axios.get(`${API}/bots/${id}/details${refresh ? "?refresh=true" : ""}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOwners(res.data.owners || []);
      setBuyers(res.data.buyers || []);
      setServers(res.data.servers || []);
      setTokenInvalid(!!res.data.token_invalid);
      mergeBot(res.data.bot);
      return true;
    } catch (error) {
      if (!refresh) {
        setOwners([]);
        setBuyers([]);
        setServers([]);
      }
      return false;
    }
  }, [mergeBot]);

  const handleRefresh = async () => {
    if (!selectedBot || refreshing) return;
    setRefreshing(true);
    const ok = await fetchBotDetails(selectedBot.id, true);
    setRefreshing(false);
    if (ok) toast.success("Logo et serveurs actualisés depuis Discord");
    else toast.error("Actualisation impossible, réessaie dans quelques secondes");
  };

  useEffect(() => {
    setNameInput(selectedBot?.bot_name || "");
    setTokenInput("");
  }, [selectedBot?.id, selectedBot?.bot_name]);

  const handleRename = async () => {
    const name = nameInput.trim().replace(/\s+/g, " ");
    if (name.length < 2 || name.length > 32) {
      toast.error("Le nom doit faire entre 2 et 32 caractères");
      return;
    }
    setNameSaving(true);
    try {
      const token = tokenStore.get();
      const res = await axios.put(`${API}/bots/${selectedBot.id}/name`, { bot_name: name }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      mergeBot({ id: selectedBot.id, bot_name: res.data.bot_name });
      toast.success("Nom du bot modifié");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de modifier le nom");
    } finally {
      setNameSaving(false);
    }
  };

  const [tokenSwitchKey, setTokenSwitchKey] = useState(0);
  const handleTokenChange = async () => {
    if (!tokenInput.trim()) {
      toast.error("Colle le nouveau token");
      return;
    }
    setTokenSaving(true);
    try {
      const token = tokenStore.get();
      const res = await axios.put(`${API}/bots/${selectedBot.id}/token`, { discord_token: tokenInput.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      mergeBot(res.data.bot);
      setTokenInput("");
      setTokenInvalid(false);
      await fetchBotDetails(selectedBot.id);
      setTokenSwitchKey((k) => k + 1);
      toast.success("Token changé : le bot redémarre avec le nouveau token");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de changer le token");
    } finally {
      setTokenSaving(false);
    }
  };

  const fetchBots = useCallback(async () => {
    try {
      const token = tokenStore.get();
      const res = await axios.get(`${API}/bots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Seuls les bots Discord (Gestion, Radio...) se gèrent ici. Les produits
      // téléchargeables (VantaPhone...) se gèrent depuis le tableau de bord.
      const discordBots = (res.data || []).filter(b => !b.downloadable && !['upgraded', 'converted'].includes(b.status));
      setBots(discordBots);

      const bot = (botId && discordBots.find(b => b.id === botId)) || discordBots[0];
      if (bot) {
        setSelectedBot(bot);
        fetchBotDetails(bot.id);
        if (botId && bot.id !== botId) navigate(`/bot-manager/${bot.id}`, { replace: true });
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des bots");
    } finally {
      setLoading(false);
    }
  }, [botId, fetchBotDetails, navigate]);

  useEffect(() => {
    fetchBots();
  }, [fetchBots]);

  const handleBotChange = (newBotId) => {
    const bot = bots.find(b => b.id === newBotId);
    if (bot) {
      setSelectedBot(bot);
      fetchBotDetails(bot.id);
      navigate(`/bot-manager/${newBotId}`);
    }
  };

  const handleAddOwner = async () => {
    if (!newOwnerId.trim()) {
      toast.error("Veuillez entrer un ID Discord");
      return;
    }
    
    try {
      const token = tokenStore.get();
      await axios.post(`${API}/bots/${selectedBot.id}/owners`, {
        discord_id: newOwnerId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOwners([...owners, { discord_id: newOwnerId, added_at: new Date().toISOString() }]);
      setNewOwnerId("");
      toast.success("Owner ajouté avec succès");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible d'ajouter cet owner");
    }
  };

  const handleAddBuyer = async () => {
    if (!newBuyerId.trim()) {
      toast.error("Veuillez entrer un ID Discord");
      return;
    }
    
    try {
      const token = tokenStore.get();
      await axios.post(`${API}/bots/${selectedBot.id}/buyers`, {
        discord_id: newBuyerId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBuyers([...buyers, { discord_id: newBuyerId, added_at: new Date().toISOString() }]);
      setNewBuyerId("");
      toast.success("Buyeur ajouté avec succès");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible d'ajouter ce buyeur");
    }
  };

  const handleRemoveUser = async () => {
    if (!deleteUser) return;
    
    try {
      const token = tokenStore.get();
      const endpoint = deleteUser.type === 'owner' ? 'owners' : 'buyers';
      await axios.delete(`${API}/bots/${selectedBot.id}/${endpoint}/${deleteUser.discord_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (deleteUser.type === 'owner') {
        setOwners(owners.filter(o => o.discord_id !== deleteUser.discord_id));
      } else {
        setBuyers(buyers.filter(b => b.discord_id !== deleteUser.discord_id));
      }
      toast.success(`${deleteUser.type === 'owner' ? 'Owner' : 'Buyeur'} supprimé`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Suppression impossible");
    } finally {
      setDeleteUser(null);
    }
  };

  const handleRenew = async () => {
    if (!selectedBot) return;
    
    setRenewLoading(true);
    
    try {
      const token = tokenStore.get();
      const response = await axios.post(`${API}/checkout/renew`, {
        bot_id: selectedBot.id,
        plan_id: selectedPlan,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du renouvellement");
      setRenewLoading(false);
    }
  };

  const isExpired = (bot) => {
    if (!bot?.expires_at) return false;
    return new Date(bot.expires_at) < new Date();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "jamais";
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getDaysLeft = (expiresAt) => {
    if (!expiresAt) return "À vie";
    const days = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
    if (days > 365 * 50) return "À vie";
    if (days < 0) return "Expiré";
    return `${days} jours`;
  };

  const botDisplay = selectedBot ? (BOT_DISPLAY[selectedBot.bot_type] || BOT_DISPLAY.gestion) : null;
  const pricingPlans = selectedBot ? BASE_PRICING.map(plan => ({
    ...plan,
    price: plan.basePrice + (botDisplay?.priceOffset || 0)
  })) : BASE_PRICING;
  const currentPlan = pricingPlans.find(p => p.id === selectedPlan);

  if (loading) {
    return (
      <div className="min-h-screen bg-vanta-base">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  if (bots.length === 0) {
    return (
      <div className="min-h-screen bg-vanta-base">
        <Navbar />
        <div className="pt-24 pb-16 max-w-4xl mx-auto px-6">
          <div className="bg-vanta-surface border border-border-default p-12 text-center">
            <Settings className="w-16 h-16 text-text-muted mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold mb-4">Aucun bot Discord à gérer</h2>
            <p className="text-text-secondary mb-2">
              Le gestionnaire sert à contrôler vos bots Discord (Gestion, Radio) : owners, buyeurs, serveurs et renouvellement.
            </p>
            <p className="text-text-muted text-sm mb-6">
              Vos scripts comme VantaPhone se gèrent depuis le tableau de bord : clé de licence, téléchargement et documentation.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                data-testid="manager-go-dashboard"
                onClick={() => navigate('/dashboard')}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-none"
              >
                Aller au tableau de bord
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/shop')}
                className="border-border-default px-6 py-3 rounded-none"
              >
                Voir les bots Discord
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vanta-base">
      <Navbar />
      
      <div className="pt-16 flex">
        {/* Sidebar */}
        <aside className="w-72 min-h-screen bg-vanta-surface border-r border-border-default fixed left-0 top-16 bottom-0 overflow-y-auto">
          {/* Bot Selector */}
          <div className="p-4 border-b border-border-default">
            <label className="block text-xs text-text-muted mb-2 font-mono">SÉLECTIONNER UN BOT</label>
            <Select value={selectedBot?.id} onValueChange={handleBotChange}>
              <SelectTrigger className="bg-vanta-base border-border-default rounded-none h-14">
                <SelectValue>
                  {selectedBot && (
                    <div className="flex items-center gap-3">
                      <img 
                        src={botLogo(selectedBot)} 
                        alt="" 
                        className="w-8 h-8 rounded-full object-cover"
                      />
                      <div className="text-left">
                        <p className="font-semibold text-sm">{selectedBot.bot_name}</p>
                        <p className={`text-xs ${isExpired(selectedBot) ? 'text-status-error' : 'text-status-success'}`}>
                          {isExpired(selectedBot) ? 'Expiré' : 'Actif'}
                        </p>
                      </div>
                    </div>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-vanta-surface border-border-default">
                {bots.map((bot) => {
                  const display = BOT_DISPLAY[bot.bot_type] || BOT_DISPLAY.gestion;
                  return (
                    <SelectItem 
                      key={bot.id} 
                      value={bot.id}
                      className="focus:bg-vanta-base py-3"
                    >
                      <div className="flex items-center gap-3">
                        <img src={bot.bot_avatar_url || display.logo} alt="" className="w-8 h-8 rounded-full object-cover" />
                        <div>
                          <p className="font-semibold">{bot.bot_name}</p>
                          <p className={`text-xs ${isExpired(bot) ? 'text-status-error' : 'text-status-success'}`}>
                            {bot.bot_type_name}
                          </p>
                        </div>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Navigation */}
          <nav className="p-4">
            <ul className="space-y-1">
              {NAV_ITEMS.map((item) => {
                // Hide renewal if not expired
                if (item.id === 'renouvellement' && selectedBot && !isExpired(selectedBot)) {
                  return null;
                }
                
                const isActive = activeSection === item.id;
                const Icon = item.icon;
                
                return (
                  <li key={item.id}>
                    <button
                      data-testid={`nav-${item.id}`}
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                        isActive 
                          ? `${botDisplay?.bgClass || 'bg-blue-500/10'} ${botDisplay?.colorClass || 'text-blue-500'} border-l-2 ${botDisplay?.borderClass || 'border-blue-500'}`
                          : 'text-text-secondary hover:bg-vanta-base hover:text-white'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                      {item.id === 'renouvellement' && isExpired(selectedBot) && (
                        <span className="ml-auto w-2 h-2 bg-status-error rounded-full"></span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Bot Status */}
          {selectedBot && (
            <div className="p-4 mt-auto border-t border-border-default">
              <div className={`p-4 ${botDisplay?.bgClass || 'bg-blue-500/10'} border ${botDisplay?.borderClass || 'border-blue-500/30'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-2 h-2 rounded-full ${isExpired(selectedBot) ? 'bg-status-error' : 'bg-status-success'}`}></div>
                  <span className="text-sm font-medium">
                    {isExpired(selectedBot) ? 'Bot expiré' : 'Bot actif'}
                  </span>
                </div>
                <p className="text-xs text-text-muted">
                  {!selectedBot.expires_at
                    ? 'Abonnement à vie'
                    : `${isExpired(selectedBot) ? 'Expiré le' : 'Expire le'} ${formatDate(selectedBot.expires_at)}`}
                </p>
                {!isExpired(selectedBot) && selectedBot.expires_at && (
                  <p className={`text-xs mt-1 ${botDisplay?.colorClass || 'text-blue-500'}`}>
                    {getDaysLeft(selectedBot.expires_at) === "À vie" ? "À vie" : `${getDaysLeft(selectedBot.expires_at)} restants`}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Back to Dashboard */}
          <div className="p-4 border-t border-border-default">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="w-full justify-start text-text-secondary hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au Dashboard
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="ml-72 flex-1 p-8">
          {/* Section: Accueil */}
          {activeSection === "accueil" && selectedBot && (
            <div className="max-w-4xl">
              <div className="flex items-center gap-4 mb-8">
                <img 
                  src={botLogo(selectedBot)} 
                  alt="" 
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <h1 className="font-heading text-3xl font-bold truncate">{selectedBot.bot_name}</h1>
                  <p className={botDisplay?.colorClass}>
                    {selectedBot.bot_type_name}
                    {selectedBot.bot_username && <span className="text-text-muted"> · {selectedBot.bot_username} sur Discord</span>}
                  </p>
                </div>
                {selectedBot.bot_type !== "radio" && (
                  <Button
                    data-testid="bot-open-dashboard"
                    onClick={() => navigate(`/bot-dashboard/${selectedBot.id}`)}
                    className="bg-violet-600 hover:bg-violet-500 text-white rounded-none"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Configurer les serveurs
                  </Button>
                )}
                <Button
                  data-testid="bot-refresh-btn"
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="border-border-default rounded-none"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
                  Actualiser
                </Button>
              </div>

              {tokenInvalid && (
                <div className="bg-status-error/10 border border-status-error/40 p-4 mb-8 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-status-error shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-sm">Discord refuse le token de ce bot</p>
                    <p className="text-text-secondary text-sm">
                      Le bot ne peut pas se connecter. Régénère le token sur le Discord Developer Portal puis change-le dans{" "}
                      <button className="underline" onClick={() => setActiveSection("parametres")}>Paramètres</button>.
                    </p>
                  </div>
                </div>
              )}

              {/* Bot Info Card */}
              <div className="bg-vanta-surface border border-border-default p-6 mb-8">
                <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                  <Shield className={`w-5 h-5 ${botDisplay?.colorClass}`} />
                  Informations du Bot
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-vanta-base p-4 border border-border-default">
                    <p className="text-text-muted text-xs font-mono mb-1">ID BOT</p>
                    <p className="font-mono text-sm truncate">{selectedBot.id.slice(0, 8)}...</p>
                  </div>
                  <div className="bg-vanta-base p-4 border border-border-default">
                    <p className="text-text-muted text-xs font-mono mb-1">TYPE</p>
                    <p className="text-sm">{selectedBot.bot_type_name}</p>
                  </div>
                  <div className="bg-vanta-base p-4 border border-border-default">
                    <p className="text-text-muted text-xs font-mono mb-1">PLAN</p>
                    <p className="text-sm">{selectedBot.plan_name}</p>
                  </div>
                  <div className="bg-vanta-base p-4 border border-border-default">
                    <p className="text-text-muted text-xs font-mono mb-1">STATUT</p>
                    <p className={`text-sm ${isExpired(selectedBot) ? 'text-status-error' : 'text-status-success'}`}>
                      {isExpired(selectedBot) ? 'Expiré' : 'Actif'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Servers */}
              <div className="bg-vanta-surface border border-border-default p-6">
                <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                  <Server className={`w-5 h-5 ${botDisplay?.colorClass}`} />
                  Serveurs ({servers.length})
                </h2>
                
                {servers.length === 0 ? (
                  <div className="text-center py-8 text-text-muted">
                    <Server className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun serveur rejoint</p>
                    <p className="text-xs mt-1">Invite le bot sur un serveur puis clique sur « Actualiser ».</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {servers.map((server) => (
                      <div 
                        key={server.id}
                        className="bg-vanta-base border border-border-default p-4 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          {server.icon_url ? (
                            <img src={server.icon_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                          ) : (
                            <div className={`w-12 h-12 rounded-full ${botDisplay?.bgClass} flex items-center justify-center font-semibold ${botDisplay?.colorClass}`}>
                              {(server.name || "?").slice(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="font-semibold">{server.name}</p>
                            <p className="text-text-muted text-sm flex items-center gap-1">
                              <Hash className="w-3 h-3" />
                              {server.id}
                            </p>
                          </div>
                        </div>
                        {server.memberCount != null && (
                          <div className="text-right">
                            <p className="text-sm text-text-secondary">
                              <Users className="w-4 h-4 inline mr-1" />
                              {server.memberCount} membres
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section: Modération */}
          {activeSection === "moderation" && selectedBot && (
            <div className="max-w-4xl">
              <h1 className="font-heading text-3xl font-bold mb-2">Modération</h1>
              <p className="text-text-secondary mb-8">Gérez les owners et buyeurs de votre bot</p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Owners */}
                <div className="bg-vanta-surface border border-border-default p-6">
                  <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                    <Crown className="w-5 h-5 text-yellow-500" />
                    Owners ({owners.length})
                  </h2>
                  
                  {/* Add Owner */}
                  <div className="flex gap-2 mb-4">
                    <Input
                      data-testid="add-owner-input"
                      placeholder="ID Discord du owner"
                      value={newOwnerId}
                      onChange={(e) => setNewOwnerId(e.target.value)}
                      className="bg-vanta-base border-border-default rounded-none flex-1"
                    />
                    <Button
                      data-testid="add-owner-btn"
                      onClick={handleAddOwner}
                      className={`${botDisplay?.btnClass || 'bg-blue-500 hover:bg-blue-600'} rounded-none px-4`}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Owners List */}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {owners.length === 0 ? (
                      <p className="text-text-muted text-sm text-center py-4">Aucun owner ajouté</p>
                    ) : (
                      owners.map((owner) => (
                        <div 
                          key={owner.discord_id}
                          className="bg-vanta-base border border-border-default p-3 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <Crown className="w-4 h-4 text-yellow-500" />
                            <span className="font-mono text-sm">{owner.discord_id}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteUser({ ...owner, type: 'owner' })}
                            className="text-status-error hover:bg-status-error/10 p-2 h-auto"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Buyers */}
                <div className="bg-vanta-surface border border-border-default p-6">
                  <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5 text-green-500" />
                    Buyeurs ({buyers.length})
                  </h2>
                  
                  {/* Add Buyer */}
                  <div className="flex gap-2 mb-4">
                    <Input
                      data-testid="add-buyer-input"
                      placeholder="ID Discord du buyeur"
                      value={newBuyerId}
                      onChange={(e) => setNewBuyerId(e.target.value)}
                      className="bg-vanta-base border-border-default rounded-none flex-1"
                    />
                    <Button
                      data-testid="add-buyer-btn"
                      onClick={handleAddBuyer}
                      className={`${botDisplay?.btnClass || 'bg-blue-500 hover:bg-blue-600'} rounded-none px-4`}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Buyers List */}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {buyers.length === 0 ? (
                      <p className="text-text-muted text-sm text-center py-4">Aucun buyeur ajouté</p>
                    ) : (
                      buyers.map((buyer) => (
                        <div 
                          key={buyer.discord_id}
                          className="bg-vanta-base border border-border-default p-3 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <ShoppingBag className="w-4 h-4 text-green-500" />
                            <span className="font-mono text-sm">{buyer.discord_id}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteUser({ ...buyer, type: 'buyer' })}
                            className="text-status-error hover:bg-status-error/10 p-2 h-auto"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section: Paramètres */}
          {activeSection === "parametres" && selectedBot && (() => {
            const nextChange = selectedBot.token_change_available_at ? new Date(selectedBot.token_change_available_at) : null;
            // Token refusé par Discord : on peut toujours le remplacer, même après 2 changements.
            const tokenLocked = !!nextChange && nextChange > new Date() && !tokenInvalid;
            return (
              <div className="max-w-2xl">
                <h1 className="font-heading text-3xl font-bold mb-2">Paramètres</h1>
                <p className="text-text-secondary mb-8">Nom et token Discord de votre bot</p>

                {/* Nom */}
                <div className="bg-vanta-surface border border-border-default p-6 mb-8">
                  <h2 className="font-heading text-xl font-semibold mb-1 flex items-center gap-2">
                    <Pencil className={`w-5 h-5 ${botDisplay?.colorClass}`} />
                    Nom du bot
                  </h2>
                  <p className="text-text-muted text-sm mb-4">Le nom affiché sur le site (2 à 32 caractères).</p>
                  <div className="flex gap-2">
                    <Input
                      data-testid="bot-name-input"
                      value={nameInput}
                      maxLength={32}
                      onChange={(e) => setNameInput(e.target.value)}
                      className="bg-vanta-base border-border-default rounded-none flex-1"
                    />
                    <Button
                      data-testid="bot-name-save"
                      onClick={handleRename}
                      disabled={nameSaving || nameInput.trim() === selectedBot.bot_name}
                      className={`${botDisplay?.btnClass || 'bg-blue-500 hover:bg-blue-600'} rounded-none px-4`}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer
                    </Button>
                  </div>
                </div>

                {/* Token */}
                <div className="bg-vanta-surface border border-border-default p-6">
                  <h2 className="font-heading text-xl font-semibold mb-1 flex items-center gap-2">
                    <KeyRound className={`w-5 h-5 ${botDisplay?.colorClass}`} />
                    Token Discord
                  </h2>
                  <p className="text-text-muted text-sm mb-2">
                    Modifiable 2 fois par jour. Le nouveau token est vérifié auprès de Discord, puis le bot
                    redémarre avec lui et se reconnecte au site (l'ancien token est arrêté).
                  </p>
                  <p className="text-text-muted text-xs mb-4">Avant de changer : dans le Discord Developer Portal &gt; Bot, active les 3 « Privileged Gateway Intents » (Presence, Server Members, Message Content), sinon le bot ne peut pas démarrer.</p>
                  {tokenLocked ? (
                    <div className="bg-vanta-base border border-border-default p-4 text-sm text-text-secondary" data-testid="bot-token-locked">
                      Token déjà changé 2 fois en 24 h. Prochain changement possible le{" "}
                      <span className="text-white">
                        {nextChange.toLocaleDateString("fr-FR", { day: "numeric", month: "long" })} à{" "}
                        {nextChange.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      </span>.
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        data-testid="bot-token-input"
                        type="password"
                        autoComplete="off"
                        value={tokenInput}
                        onChange={(e) => setTokenInput(e.target.value)}
                        placeholder="Nouveau token (Developer Portal > Bot > Reset Token)"
                        className="bg-vanta-base border-border-default rounded-none flex-1 font-mono"
                      />
                      <Button
                        data-testid="bot-token-save"
                        onClick={handleTokenChange}
                        disabled={tokenSaving || !tokenInput.trim()}
                        className={`${botDisplay?.btnClass || 'bg-blue-500 hover:bg-blue-600'} rounded-none px-4`}
                      >
                        {tokenSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Changer"}
                      </Button>
                    </div>
                  )}
                  {!tokenLocked && selectedBot.token_changes_left === 1 && (
                    <p className="text-xs text-text-muted mt-2" data-testid="bot-token-left">Encore 1 changement possible aujourd'hui.</p>
                  )}
                  <TokenSwitchStatus botId={selectedBot.id} refreshKey={tokenSwitchKey} />
                </div>
              </div>
            );
          })()}

          {/* Section: Renouvellement */}
          {activeSection === "renouvellement" && selectedBot && isExpired(selectedBot) && (
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-2">
                <AlertTriangle className="w-8 h-8 text-status-warning" />
                <h1 className="font-heading text-3xl font-bold">Renouvellement</h1>
              </div>
              <p className="text-text-secondary mb-8">
                Votre bot a expiré le {formatDate(selectedBot.expires_at)}. Renouvelez-le pour continuer à l'utiliser.
              </p>

              <div className={`bg-vanta-surface border ${botDisplay?.borderClass} p-8`}>
                {/* Bot Info */}
                <div className="flex items-center gap-4 mb-8 pb-6 border-b border-border-default">
                  <img src={botLogo(selectedBot)} alt="" className="w-16 h-16 rounded-full object-cover opacity-50" />
                  <div>
                    <p className="font-heading text-xl font-bold">{selectedBot.bot_name}</p>
                    <p className="text-status-error text-sm">Expiré depuis {Math.abs(Math.ceil((new Date(selectedBot.expires_at) - new Date()) / (1000 * 60 * 60 * 24)))} jours</p>
                  </div>
                </div>

                {/* Plan Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-white mb-3">
                    Choisir la durée de renouvellement
                  </label>
                  <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                    <SelectTrigger 
                      data-testid="renew-plan-select"
                      className="bg-vanta-base border-border-default rounded-none h-14 text-lg"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-vanta-surface border-border-default">
                      {pricingPlans.map((plan) => (
                        <SelectItem 
                          key={plan.id} 
                          value={plan.id}
                          className="focus:bg-vanta-base py-3"
                        >
                          <span className="flex items-center gap-3 text-base">
                            <span className="text-xl">{plan.badge}</span>
                            <span className="font-bold">{plan.price.toFixed(2)}€</span>
                            <span className="text-text-muted">-</span>
                            <span>{plan.duration}</span>
                            {plan.savings && (
                              <span className={`text-sm ${botDisplay?.colorClass}`}>({plan.savings})</span>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Summary */}
                {currentPlan && (
                  <div className={`bg-vanta-base border ${botDisplay?.borderClass} p-4 mb-6`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{currentPlan.badge}</span>
                        <span className="font-heading font-bold">{currentPlan.name}</span>
                      </div>
                      <p className={`font-heading text-2xl font-bold ${botDisplay?.colorClass}`}>
                        {currentPlan.price.toFixed(2)}€
                      </p>
                    </div>
                    <div className="flex gap-4 text-sm text-text-secondary">
                      <span className="flex items-center gap-1">
                        <Check className={`w-4 h-4 ${botDisplay?.colorClass}`} />
                        Activation immédiate
                      </span>
                      <span className="flex items-center gap-1">
                        <Check className={`w-4 h-4 ${botDisplay?.colorClass}`} />
                        Support 24/7
                      </span>
                    </div>
                  </div>
                )}

                {/* Renew Button */}
                <Button
                  data-testid="renew-btn"
                  onClick={handleRenew}
                  disabled={renewLoading}
                  className={`w-full py-6 rounded-none font-bold text-lg ${botDisplay?.btnClass} text-white`}
                >
                  {renewLoading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Renouveler pour {currentPlan?.price.toFixed(2)}€
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <AlertDialogContent className="bg-vanta-surface border-border-default">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Supprimer {deleteUser?.type === 'owner' ? "l'owner" : "le buyeur"} ?</AlertDialogTitle>
            <AlertDialogDescription className="text-text-secondary">
              Êtes-vous sûr de vouloir retirer <span className="text-white font-mono">{deleteUser?.discord_id}</span> ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border-default hover:bg-vanta-base rounded-none">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRemoveUser}
              className="bg-status-error hover:bg-status-error/80 rounded-none"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
